import React from "react";
import { Link } from "react-router-dom";
import { Star, MapPin, Wifi, Car, Utensils, Dumbbell } from "lucide-react";
import { Hotel } from "../types";

interface HotelCardProps {
  hotel: Hotel;
}

const HotelCard: React.FC<HotelCardProps> = ({ hotel }) => {
  const renderStars = (stars: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < stars ? "text-yellow-400 fill-current" : "text-gray-300"
        }`}
      />
    ));
  };

  const getAmenityIcon = (amenity: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      "Free WiFi": <Wifi className="w-4 h-4" />,
      Pool: <div className="w-4 h-4 bg-blue-500 rounded-full"></div>,
      Restaurant: <Utensils className="w-4 h-4" />,
      "Fitness Center": <Dumbbell className="w-4 h-4" />,
      "Airport Shuttle": <Car className="w-4 h-4" />,
    };
    return (
      iconMap[amenity] || (
        <div className="w-4 h-4 bg-gray-400 rounded-full"></div>
      )
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 hover-lift fade-in">
      {/* Hotel Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={hotel.images[0]}
          alt={hotel.name}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-full px-3 py-1 flex items-center space-x-1">
          <Star className="w-4 h-4 text-yellow-400 fill-current" />
          <span className="text-sm font-semibold">{hotel.rating}</span>
        </div>
      </div>

      {/* Hotel Info */}
      <div className="p-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="text-xl font-bold text-gray-900 mb-1">
              {hotel.name}
            </h3>
            <div className="flex items-center text-gray-600 mb-2">
              <MapPin className="w-4 h-4 mr-1" />
              <span className="text-sm">{hotel.location}</span>
            </div>
            <div className="flex items-center space-x-1 mb-2">
              {renderStars(hotel.stars)}
              <span className="text-sm text-gray-600 ml-2">
                ({hotel.reviewCount} reviews)
              </span>
            </div>
          </div>
        </div>

        {/* Amenities */}
        <div className="flex flex-wrap gap-2 mb-4">
          {hotel.amenities.slice(0, 4).map((amenity, index) => (
            <div
              key={index}
              className="flex items-center space-x-1 text-gray-600 text-sm"
            >
              {getAmenityIcon(amenity)}
              <span>{amenity}</span>
            </div>
          ))}
          {hotel.amenities.length > 4 && (
            <span className="text-sm text-gray-500">
              +{hotel.amenities.length - 4} more
            </span>
          )}
        </div>

        {/* Price and Action */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-gray-900">
              ${hotel.price}
              <span className="text-sm font-normal text-gray-600">/night</span>
            </div>
            <div className="text-sm text-gray-600">per room</div>
          </div>
          <Link to={`/hotel/${hotel.id}`} className="btn btn-primary">
            View Details
          </Link>
        </div>
      </div>
    </div>
  );
};

export default HotelCard;
